<template>
  <div class="pb-12">
    <v-progress-linear class="section-progress-stepper" :value="progressCount" />
    <v-row>
      <v-col cols="12" md="6" class="pl-md-10">
        <transition-group
          enter-active-class="fadeIn"
          leave-active-class="fadeOutFast"
          mode="out-in"
        >
          <template v-for="(question, index) in questions">
            <div
              v-if="checkVisible(question)"
              :id="'question-' + question.id"
              :key="question.id"
            >
              <template v-if="question.type === 'radio'">
                <radio-question
                  v-if="question.id !== 6"
                  :question="question"
                  :parent-answer="getParentAnswer(question)"
                  :current-answer="getAnswer(question)"
                  :answers="answers"
                  class="question-box mb-5"
                  @selectAnswer="addAnswer($event, index)"
                />
                <radio-question
                  v-else-if="question.id === 6 && roadTaxType"
                  :question="question"
                  :parent-answer="getParentAnswer(question)"
                  :current-answer="getAnswer(question)"
                  :answers="answers"
                  :road-tax-type="roadTaxType"
                  :exchange-rate="eurMdlExchangeRate"
                  class="question-box mb-5"
                  @selectAnswer="addAnswer($event, index)"
                />
              </template>
              <dropdown-question
                v-else-if="question.type === 'dropdown'"
                :question="question"
                :current-answer="getAnswer(question)"
                :parent-answer="getParentAnswer(question)"
                :answers="answers"
                :road-tax-answer="roadTaxAnswerId"
                class="question-box mb-5"
                @selectAnswer="addAnswer($event, index)"
              />
            </div>
          </template>
        </transition-group>
      </v-col>
      <v-col cols="12" md="6" class="d-flex align-center justify-center">
        <transition
          enter-active-class="fadeIn"
          leave-active-class="fadeOut"
          mode="out-in"
        >
          <div
            v-if="completedSection"
            id="total-section"
            :key="'total-amount'"
            class="d-flex flex-column justify-center align-center"
          >
            <div v-if="roadTaxAmount > 0" class="mb-2">
              <div class="text-center">
                Carte verde:
              </div>
              <div class="partial-amount text-center">
                {{ baseGreenCardAmount }} LEI
              </div>
            </div>
            <div v-if="roadTaxAmount > 0">
              <div class="text-center">
                Rovinieta:
              </div>
              <div class="partial-amount text-center">
                {{ parsedRoadTaxAmount }} LEI
              </div>
            </div>
            <div v-if="roadTaxAmount > 0" class="hz-divider my-2"></div>
            <div>
              <div class="mb-31 text-center">
                Total spre plata:
              </div>
              <div class="total-amount text-center">
                {{ totalAmount }} LEI
              </div>
            </div>
            <div class="mt-5">
              <v-btn
                color="#df0100"
                class="base-button"
                depressed
                tile
                dark
                large
                @click="$emit('nextStep', dataLayerEvent)"
              >
                Comanda asigurare
                <v-icon right>
                  mdi-arrow-right-circle
                </v-icon>
              </v-btn>
            </div>
          </div>
        </transition>
      </v-col>
    </v-row>
    <div id="bottom"></div>
  </div>
</template>

<script>
import { CONSTANTS, getQuestions, greenCardPrices } from '@/_utils/static-data';
import RadioQuestion from '@/components/RadioQuestion';
import DropdownQuestion from '@/components/DropdownQuestion';
import { getExchangeRate } from '@/_utils/helpers';
export default {
  name: 'GreenCardCalculator',
  components: {
    DropdownQuestion,
    RadioQuestion
  },
  data() {
    return {
      answers: [],
      lastAnswerIndex: 1,
      eurMdlExchangeRate: '20.00',
      roadTaxAmount: 0
    };
  },
  computed: {
    dataLayerEvent() {
      return this.roadTaxAmount > 0 ? CONSTANTS.greenCardEventOrderWithRoadTax : CONSTANTS.greenCardEventOrder;
    },
    questions() {
      const allQuestions = getQuestions('greenCard', 'calculator');
      let visibleQuestions = [];
      for (const question of allQuestions) {
        if (!question.parent) {
          visibleQuestions.push(question);
        } else if (question.parent && question.visibleIf) {
          const visibleIfParent = this.answers.find(el => el.questionId === question.visibleIf.questionId);
          if (visibleIfParent?.id === question.visibleIf.answer) {
            const parentAnswer = this.answers.find(el => el.questionId === question.parent);
            const questionOptions = question.filterable
              ? question.options.filter(el => el.parentId === parentAnswer?.id || !el.parentId)
              : question.options;
            if (questionOptions.length > 0) {
              visibleQuestions.push(question);
            }
          }
        } else if (question.parent && !question.visibleIf) {
          const parentAnswer = this.answers.find(el => el.questionId === question.parent);
          if (parentAnswer) {
            visibleQuestions.push(question);
          }
        }
      }
      return visibleQuestions;
    },
    progressCount() {
      return (this.answers.length / this.totalQuestions) * 100;
    },
    totalQuestions() {
      return this.questions.length;
    },
    isMobile() {
      return this.$vuetify.breakpoint.width < 961;
    },
    roadTaxAnswerId() {
      return this.answers.find(el => el?.questionId === 4)?.id || null;
    },
    roadTaxType() {
      let vehicleRoadTypeAnswer = this.answers.find(el => el.questionId === 3)?.roadTaxValue || null;
      if (!vehicleRoadTypeAnswer) {
        vehicleRoadTypeAnswer = this.answers.find(el => el.questionId === 5)?.roadTaxValue || null;
      }
      return vehicleRoadTypeAnswer;
    },
    baseGreenCardAmount() {
      return parseFloat(this.calculateGreenCard(this.answers) * parseFloat(this.eurMdlExchangeRate)
        .toFixed(2)).toFixed(2);
    },
    parsedRoadTaxAmount() {
      return parseFloat(this.roadTaxAmount).toFixed(2);
    },
    totalAmount() {
      const defaultGreenCard = this.calculateGreenCard(this.answers) * parseFloat(this.eurMdlExchangeRate).toFixed(2);
      return parseFloat(defaultGreenCard + this.roadTaxAmount).toFixed(2);
    },
    completedSection() {
      return this.totalQuestions === this.answers.length;
    }
  },
  async mounted() {
    this.eurMdlExchangeRate = await getExchangeRate();
  },
  methods: {
    getAnswer(question) {
      return this.answers.find(el => el.questionId === question.id);
    },
    getParentAnswer(question) {
      return question.parent ? this.answers.find(el => el.questionId === question.parent) : null;
    },
    removeChildren(children) {
      if (!children) { return; }
      for (const childId of children) {
        this.removeAnswer(childId);
      }
    },
    checkVisible(question) {
      return this.lastAnswerIndex >= question.order;
    },
    removeAnswer(questionId) {
      this.answers = this.answers.filter(el => el.questionId !== questionId);
      if (questionId === 6) {
        this.roadTaxAmount = 0;
      }
      this.$emit('updateAnswers', {
        answers: JSON.parse(JSON.stringify(this.answers)),
        isValid: this.completedSection
      });
    },
    addAnswer(answer, index) {
      const isAnswered = !!this.answers.find(el => el.questionId === answer.questionId);
      const currentQuestion = this.questions.find(el => el.id === answer.questionId);
      if (isAnswered) {
        this.answers = this.answers.map((el) => {
          if (el.questionId === answer.questionId) {
            el = answer;
          }
          return el;
        });
        this.removeChildren(currentQuestion.children);
        // SPECIAL CASE FOR OPTIONAL QUESTIONS
        if (answer.questionId === 4) {
          this.lastAnswerIndex = 6;
          if (this.isMobile && !this.completedSection) {
            this.scrollToNext(5);
          }
          // REMOVE IF ROAD TAX AND TYPE MOTO
          const vehicleTypeAnswer = this.answers.find(el => el.questionId === 3)?.id;
          if (vehicleTypeAnswer === 6) {
            this.removeAnswer(3);
          }
          this.removeChildren([5, 6]);
        }
        if (answer.questionId === 3 || answer.questionId === 5) {
          this.removeAnswer(6);
        }
        if (answer.questionId === 1 && (answer.id === 1 || answer.id === 2)) {
          this.removeChildren([4, 5, 6]);
        }
      } else {
        if (answer.questionId === 1 && !this.isMobile) {
          this.$emit('formStarted');
        }
        this.answers.push(answer);
        const nextQuestion = this.questions[index + 1];
        if (nextQuestion?.order > this.lastAnswerIndex) {
          this.lastAnswerIndex = nextQuestion.order;
        } else if (currentQuestion?.order >= this.lastAnswerIndex) {
          this.lastAnswerIndex = currentQuestion.order + 1;
        }
        if (this.isMobile && !this.completedSection) {
          this.scrollToNext(this.lastAnswerIndex);
        }
      }
      if (answer?.roadTaxAmount) {
        this.roadTaxAmount = parseFloat(answer.roadTaxAmount);
      }
      setTimeout(() => {
        this.$emit('updateAnswers', {
          answers: JSON.parse(JSON.stringify(this.answers)),
          isValid: this.completedSection,
          total: this.totalAmount
        });
      }, 200);
      // event for price shown
      if (this.completedSection) {
        const payload = {};
        if (this.roadTaxAmount > 0) {
          payload.cv_road_tax_amount = this.parsedRoadTaxAmount;
          payload.cv_amount = this.baseGreenCardAmount;
        }
        this.$emit('trackEvent', {
          eventName: CONSTANTS.greenCardPriceShow,
          payload
        });
      }
      if (this.completedSection && this.isMobile) {
        this.scrollToTotal();
      }
    },
    scrollToNext(nextId) {
      setTimeout(() => {
        this.$scrollTo(
          `#question-${nextId}`,
          400,
          {
            offset: -180
          });
      }, 400);
    },
    scrollToTotal() {
      setTimeout(() => {
        this.$scrollTo(
          '#total-section',
          400,
          {
            offset: -180
          });
      }, 400);
    },
    calculateGreenCard(answers) {
      const zoneAnswerId = answers.find(el => el.questionId === 1)?.id || null;
      const durationAnswerId = answers.find(el => el.questionId === 2)?.id || null;
      const vehicleTypeAnswerId = answers.find(el => el.questionId === 3)?.id || null;
      if (zoneAnswerId && durationAnswerId && vehicleTypeAnswerId) {
        const tableKey = `zone${zoneAnswerId}`;
        return greenCardPrices[tableKey][vehicleTypeAnswerId - 1][durationAnswerId - 1];
      }
      return 0;
    }
  }
};
</script>

<style scoped>
.section-progress-stepper {
  position: absolute;
  top: 0;
  left: 0;
}
.total-amount {
  font-size: 32px;
  font-weight: 600;
}
.partial-amount {
  font-size: 20px;
  font-weight: 600;
}
.hz-divider {
  height: 2px;
  width: 100%;
  background-color: #E7EFFD;
}
.question-box {
  width: 100% !important;
  background-color: #E7EFFD;
  padding: 8px;
  border-radius: 4px 4px 4px 4px;
}
</style>
